// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { PiWarningCircleLight } from "react-icons/pi";
import { Link } from "react-router-dom";
import { SMS_ENABLED } from "../../config";
import useRedirectIfAuthenticated from "../../hooks/useRedirectIfAuthenticated";
import options from "../../data/options.json";
import { serverInstance } from "../../service/axios";
import { handleApiError } from "../../utils/ErrorMessageUtils";
import "./sign.scss";

const SignUp = ({ data, language }) => {
  useRedirectIfAuthenticated();

  const getLangCode = () => {
    if (
      language === "en" ||
      language === "ko" ||
      language === "jp" ||
      language === "de" ||
      language === "se"
    )
      return "en";
    return "mn";
  };

  const [loading, setLoading] = useState(false); //TODO
  const [errorMsg, setErrorMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [optionsData, setOptionsData] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    org_name: "",
    number_of_students: 100,
    number_of_english_teachers: 1,
    first_name: "",
    last_name: "",
    email: "",
    mobile_ext: "+976",
    mobile_number: "",
    address_line_1: "",
    address_line_2: "",
    address_town: "",
    address_state: "",
    address_country: "Монгол",
    workplace: "",
    password: "",
    repassword: "",
    agreedTC: false,
    position: "",
  });
  // Add these states after existing useState declarations
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const formTopRef = useRef(null);
  const isInitialStepMount = useRef(true);

  // Load saved form data from localStorage on component mount
  useEffect(() => {
    const savedFormData = localStorage.getItem("signupFormData");
    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);
        setFormData(parsedData);
      } catch (error) {
        console.error("Error parsing saved form data:", error);
        localStorage.removeItem("signupFormData");
      }
    }
    // Mark initial load as complete
    setIsInitialLoad(false);
  }, []);

  // Save form data to localStorage whenever formData changes (but not on initial load)
  useEffect(() => {
    // Skip saving on initial load
    if (isInitialLoad) return;
    
    // Only save if form has meaningful data (not just initial empty state)
    const hasData = Object.values(formData).some(value => 
      value !== "" && value !== 0 && value !== false && value !== "+976"
    );
    
    if (hasData) {
      localStorage.setItem("signupFormData", JSON.stringify(formData));
    }
  }, [formData, isInitialLoad]);

  // Cleanup function to clear localStorage on component unmount (optional)
  useEffect(() => {
    return () => {
      // Only clear if user is not in the middle of signup process
      // This prevents clearing data when user navigates between steps
      // localStorage will be cleared after successful signup in handleSubmit
    };
  }, []);

  useEffect(() => {
    if (language && options[language]) {
      setOptionsData(options[language]);
    }
  }, [language, options]);

  useEffect(() => {
    if (isInitialStepMount.current) {
      isInitialStepMount.current = false;
      return;
    }
    formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const nextStep = async () => {
    if (validate()) {
      if (step === 3) {
        // Check if mobile number and email are already registered before sending OTP
        const isMobileAvailable = await checkMobileRegistration();
        if (!isMobileAvailable) {
          return; // Stop if mobile is already registered
        }

        const isEmailAvailable = await checkEmailRegistration();
        if (!isEmailAvailable) {
          return; // Stop if email is already registered
        }

        if (!SMS_ENABLED) {
          setStep(4);
          return;
        }

        // If both mobile and email are available, proceed with OTP
        if (!otpSent && !otpVerified) {
          sendOTP();
          return;
        }
        if (otpVerified) {
          setStep((prev) => prev + 1);
        }
      } else if (step < 5) {
        setStep((prev) => prev + 1);
      }
    }
  };

  const previousStep = () => {
    if (step > 1) setStep((prev) => prev - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    
    // Convert email to lowercase before sending
    const apiData = {
      ...formData,
      email: formData.email.toLowerCase(),
      lang: getLangCode()
    };
    
    serverInstance
      .post("/school/signup", apiData)
      .then((response) => {
        if (response.status === 200) {
          // Clear saved form data from localStorage after successful signup
          localStorage.removeItem("signupFormData");
          localStorage.setItem("lingoPortalToken", response.data.token);
          setStep(5);
        }
        else {
          //TODO
          setErrorMsg(response.error.message)
        }
      })
      .catch((err) => {
        setErrorMsg(
          err?.response?.data?.message || "Бүртгүүлэхэд алдаа гарлаа"
        );
      })
      .finally(() => setLoading(false));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrorMsg("");
  };

  const validate = () => {
    if (step === 1) {
      if (!formData.org_name.trim()) {
        setErrorMsg(data.org_required);
        return false;
      }

      return true;
    }
    if (step === 2) {
      if (formData.number_of_english_teachers === undefined) {
        setErrorMsg(data.englishTeachersRequired);
        return false;
      }
      if (!formData.number_of_students === undefined) {
        setErrorMsg(data.totalStudentsRequired);
        return false;
      }
      return true;
    }

    if (step === 3) {
      const mobilePattern = /^[0-9]{8}$/;
      if (!formData.last_name.trim()) {
        setErrorMsg(data.last_name_required);
        return false;
      }
      if (!formData.first_name.trim()) {
        setErrorMsg(data.first_name_required);
        return false;
      }
      if (!formData.position) {
        setErrorMsg(data.job_position_required);
        return false;
      }
      
      // Mobile number is mandatory, email is optional
      if (!formData.mobile_number.trim()) {
        setErrorMsg(data.mobile_number_required || "Утасны дугаар оруулна уу");
        return false;
      }
      
      // Validate mobile number format
      if (!mobilePattern.test(formData.mobile_number)) {
        setErrorMsg(data.invalid_mobile);
        return false;
      }
      
      return true;
    }

    if (step === 4) {
      const minLength = 8;
      const hasUpperCase = /[A-Z]/.test(formData.password);
      const hasLowerCase = /[a-z]/.test(formData.password);
      const hasNumbers = /\d/.test(formData.password);

      if (formData.password.length < minLength) {
        setErrorMsg(data.passwordLength);
        return false;
      }
      if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
        setErrorMsg(data.passwordRequirement);
        return false;
      }
      if (formData.password !== formData.repassword) {
        setErrorMsg(data.passwordNotMatch);
        return false;
      }
      return true;
    }

    return true;
  };

  // Check if mobile number is already registered
  const checkMobileRegistration = async () => {
    setVerificationLoading(true);
    setErrorMsg("");

    try {
      const response = await serverInstance.post("/school/mobile-registered", {
        mobile_ext: formData.mobile_ext,
        mobile_number: formData.mobile_number
      });

      if (response.status === 200) {
        if (response.data.message === "REGISTERED") {
          setErrorMsg(data.mobile_already_registered || "Энэ утасны дугаар аль хэдийн бүртгэгдсэн байна");
          return false;
        } else {
          return true; // Mobile number is available
        }
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message;
      setErrorMsg(handleApiError(data, errorMessage));
      return false;
    } finally {
      setVerificationLoading(false);
    }
    return false;
  };

  // Check if email is already registered (only if email is provided)
  const checkEmailRegistration = async () => {
    if (!formData.email.trim()) {
      return true; // Email is optional, so skip check if empty
    }

    setVerificationLoading(true);
    setErrorMsg("");

    try {
      const response = await serverInstance.post("/school/email-registered", {
        email: formData.email.toLowerCase()
      });

      if (response.status === 200) {
        if (response.data.message === "REGISTERED") {
          setErrorMsg(data.email_already_registered || "Энэ Мэйл хаяг аль хэдийн бүртгэгдсэн байна");
          return false;
        } else {
          return true; // Email is available
        }
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message;
      setErrorMsg(handleApiError(data, errorMessage));
      return false;
    } finally {
      setVerificationLoading(false);
    }
    return false;
  };

  const sendOTP = async () => {
    setOtpLoading(true);
    setErrorMsg("");

    try {
      const response = await serverInstance.post("/sms/send", {
        mobile_ext: formData.mobile_ext,
        mobile_number: formData.mobile_number,
        lang: getLangCode()
      });

      if (response.status === 200) {
        setOtpSent(true);
        setErrorMsg("");
        // Navigate to verify OTP page
        setStep(4.5); // Use 4.5 as verify OTP step
      }
    } catch (err) {
      // Handle API error messages using shared method
      const errorMessage = err?.response?.data?.message;
      setErrorMsg(handleApiError(data, errorMessage));
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP code
  const verifyOTP = async () => {
    if (!otpCode.trim()) {
      setErrorMsg(data.ENTER_OTP || "Баталгаажуулах кодыг оруулна уу");
      return;
    }

    setOtpLoading(true);
    setErrorMsg("");

    try {
      const response = await serverInstance.post("/sms/verify", {
        mobile_ext: formData.mobile_ext,
        mobile_number: formData.mobile_number,
        code: otpCode,
      });
      
      if (response.status === 200) {
        setOtpVerified(true);
        setErrorMsg("");
        // Go directly to password step after successful verification
        setStep(4);
      } else {
        setErrorMsg(handleApiError(data, response.data?.message));
      }
    } catch (err) {
      console.log(err);
      const errorMessage = err?.response?.data?.message;
      setErrorMsg(handleApiError(data, errorMessage));
    } finally {
      setOtpLoading(false);
    }
  };
  return (
    <div className="sign-container">
      <div className="sign-container-card" ref={formTopRef}>
        <div className="sign-line-container">
          <div className="sign-line" />
          {step > 1 ? (
            <div className="sign-line" />
          ) : (
            <div className="sign-line2" />
          )}
          {step > 2 ? (
            <div className="sign-line" />
          ) : (
            <div className="sign-line2" />
          )}
          {step > 3 ? (
            <div className="sign-line" />
          ) : (
            <div className="sign-line2" />
          )}
          {step >= 4 && step !== 4.5 ? (
            <div className="sign-line" />
          ) : (
            <div className="sign-line2" />
          )}
        </div>
        <h2>
          {step === 1
            ? data.hello
            : step > 1 && step < 5
            ? data.signup
            : data.signed_up}
        </h2>

        <div>
          {/* {loading ? <Dimmer /> : null} */}
          {step === 1 && (
            <form onSubmit={handleSubmit}>
              <div className="sign-container-card-content" style={{ paddingBottom: 0 }}>
                <img
                  className="sponsorsImg"
                  src="/images/signup.png"
                  style={{ width: 276 }}
                  alt=""
                />
              </div>
              {data.registrationIntro ? (
                <p className="sign-info-text">{data.registrationIntro}</p>
              ) : null}
              <h5>{data.organizationInfo || data.schoolInfo}</h5>
              <br />

              <div className="form-div">
                <label className="sign-label">{data.org_name}</label>
                <input
                  type="text"
                  name="org_name"
                  value={formData.org_name}
                  onChange={handleChange}
                  required
                  className="sign-input"
                />
              </div>
              <div className="form-div">
                <label className="sign-label">
                  {data.address_state}{" "}
                  <span className="sign-optional">
                    {data.optionalShort || "(заавал биш)"}
                  </span>
                </label>
                <select
                  name="address_state"
                  value={formData.address_state}
                  onChange={handleChange}
                  className="sign-select"
                >
                  {optionsData?.regions?.map((el, i) => (
                    <option key={i} value={el.name}>
                      {el.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-div">
                <label className="sign-label">
                  {data.address_town}{" "}
                  <span className="sign-optional">
                    {data.optionalShort || "(заавал биш)"}
                  </span>
                </label>
                <input
                  type="text"
                  name="address_town"
                  value={formData.address_town}
                  onChange={handleChange}
                  className="sign-input"
                />
              </div>
              <div className="form-div">
                <label className="sign-label">
                  {data.address_line_2}{" "}
                  <span className="sign-optional">
                    {data.optionalShort || "(заавал биш)"}
                  </span>
                </label>
                <input
                  type="text"
                  name="address_line_2"
                  value={formData.address_line_2}
                  onChange={handleChange}
                  className="sign-input"
                />
              </div>
              <div className="form-div">
                <label className="sign-label">
                  {data.address_line_1}{" "}
                  <span className="sign-optional">
                    {data.optionalShort || "(заавал биш)"}
                  </span>
                </label>
                <input
                  type="text"
                  name="address_line_1"
                  value={formData.address_line_1}
                  onChange={handleChange}
                  className="sign-input"
                />
              </div>
              <br />
              <h5>{data.changeInfo}</h5>
              {errorMsg ? (
                <div className="form-div error-msg">
                  <PiWarningCircleLight /> {errorMsg}
                </div>
              ) : null}
              <div className="form-div-row error">
                <br />
                <button
                  type="button"
                  className="sign-button"
                  onClick={nextStep}
                >
                  {data.continue}
                </button>
              </div>
              <div className="form-div signup-link sign-signin-link">
                <Link to="/school/signin" className="sign-text">
                  <button type="button" className="sign-button2">
                    {data.signin}
                  </button>
                </Link>
              </div>
              <br />
            </form>
          )}
          {step === 2 && (
            <form onSubmit={handleSubmit}>
              <h5>{data.englishCourse}</h5>
              <br />
              <div className="form-div">
                <label className="sign-label">{data.englishTeachers}</label>
                <select
                  name="number_of_english_teachers"
                  value={formData.number_of_english_teachers}
                  onChange={handleChange}
                  required
                  className="sign-select"
                >
                  {optionsData?.numTeachers?.map((el, i) => (
                    <option key={i} value={el.value}>
                      {el.text}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-div">
                <label className="sign-label">{data.totalStudents}</label>
                <select
                  name="number_of_students"
                  value={formData.number_of_students}
                  onChange={handleChange}
                  required
                  className="sign-select"
                >
                  {optionsData?.numStudents?.map((el, i) => (
                    <option key={i} value={el.value}>
                      {el.text}
                    </option>
                  ))}
                </select>
              </div>
              <br />
              <h5>{data.changeInfo}</h5>

              {errorMsg ? (
                <div className="form-div error-msg">
                  <PiWarningCircleLight /> {errorMsg}
                </div>
              ) : null}

              <div className="form-div-row error">
                <br />
                <button
                  type="button"
                  className="sign-button"
                  onClick={nextStep}
                >
                  {data.continue}
                </button>
              </div>
              <div className="form-div-row error">
                <br />
                <button
                  type="button"
                  className="sign-button3"
                  onClick={previousStep}
                >
                  {data.back}
                </button>
              </div>
              <br />
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleSubmit}>
              <h5>{data.staffInfo}</h5>
              <br />
              <div className="flex-row">
                <div className="form-div" style={{ marginRight: "10px" }}>
                  <label className="sign-label">{data.last_name}</label>
                  <input
                    type="text"
                    name="last_name"
                    required
                    value={formData.last_name}
                    onChange={handleChange}
                    className="sign-input"
                  />
                </div>
                <div className="form-div" style={{ marginLeft: "10px" }}>
                  <label className="sign-label">{data.first_name}</label>
                  <input
                    type="text"
                    name="first_name"
                    required
                    value={formData.first_name}
                    onChange={handleChange}
                    className="sign-input"
                  />
                </div>
              </div>

              <div className="form-div">
                <p className="requirement-message">
                  {data.email_is_optional}
                </p>
                <label className="sign-label">{data.email}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="sign-input"
                />
              </div>
              <div className="form-div">
                <label className="sign-label">{data.mobile_number}</label>
                <input
                  type="tel"
                  required
                  name="mobile_number"
                  pattern="[0-9]{8}"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  className="sign-input"
                />
              </div>

              <div className="form-div">
                <label className="sign-label">{data.job_position}</label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  required
                  className="sign-select"
                >
                  {optionsData?.positions?.map((el, i) => (
                    <option key={i} value={el.position}>
                      {el.position}
                    </option>
                  ))}
                </select>
              </div>
              <br />
              <h5>{data.changeInfo}</h5>

              {errorMsg ? (
                <div className="form-div error-msg">
                  <PiWarningCircleLight /> {errorMsg}
                </div>
              ) : null}
              <div>
                <br />
                <button
                  type="button"
                  className="sign-button"
                  onClick={nextStep}
                  disabled={verificationLoading}
                >
                  {verificationLoading ? (
                    <AiOutlineLoading3Quarters size={20} className="loading-spinner" />
                  ) : (
                    data.signup
                  )}
                </button>
              </div>
              <div className="form-div-row error">
                <br />
                <button
                  type="button"
                  className="sign-button3"
                  onClick={previousStep}
                >
                  {data.back}
                </button>
              </div>
            </form>
          )}

          {step === 4.5 && (
            <form>
              <h5>{data.verifyMobNumber}</h5>
              <p>
                {data.sentVerification} {formData.mobile_number}
              </p>

              <div className="form-div">
                <label className="sign-label">{data.enterVerification}</label>
                <input
                  type="text"
                  placeholder="6 оронтой код оруулна уу"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  className="sign-input"
                  maxLength={6}
                />
              </div>

              {errorMsg ? (
                <div className="form-div error-msg">
                  <PiWarningCircleLight /> {errorMsg}
                </div>
              ) : null}
              <br />
              <div className="form-div-row error">
                <button
                  type="button"
                  className="sign-button3"
                  onClick={() => setStep(3)}
                >
                  {data.back}
                </button>
              </div>
              <br />
              <div className="form-div-row error">
                <button
                  type="button"
                  onClick={verifyOTP}
                  disabled={otpLoading}
                  className="sign-button"
                >
                  {otpLoading ? (
                    <AiOutlineLoading3Quarters size={20} className="loading-spinner" />
                  ) : (
                    data.verify
                  )}
                </button>
              </div>
              <br />
              <p
                type="button"
                onClick={sendOTP}
                disabled={otpLoading}
                style={{ justifySelf: "center" }}
              >
                {data.resend || "Дахин код явуулах"}
              </p>
            </form>
          )}

          {step === 4 && (
            <form onSubmit={handleSubmit}>
              <h5>{data.password}</h5>
              <div className="form-div">
                <label className="sign-label">{data.password}</label>
                <div className="password-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="sign-input"
                  />
                  {showPassword ? (
                    <FaRegEye
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="password-toggle"
                    />
                  ) : (
                    <FaRegEyeSlash
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="password-toggle"
                    />
                  )}
                </div>
              </div>
              <div className="form-div">
                <label className="sign-label">{data.repassword}</label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="repassword"
                  value={formData.repassword}
                  onChange={handleChange}
                  required
                  className="sign-input"
                />
              </div>

              <br />
              <h5>{data.passwordInfo}</h5>
              {/* <div className="form-div">
                <br />
                <div
                  onClick={() =>
                    setFormData({ ...formData, agreedTC: !formData.agreedTC })
                  }
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {formData.agreedTC ? (
                    <RiCheckboxCircleFill color="green" size={30} />
                  ) : (
                    <RiCheckboxBlankCircleLine size={30} />
                  )}
                  <div style={{ marginLeft: "15px" }}>
                    <span
                      onClick={() =>
                        setFormData({ ...formData, agreedTC: true })
                      }
                    >
                      {data.terms}
                    </span>

                    <Link to={"/terms"} target="_blank" className="terms-link">
                      {data.link}
                    </Link>
                  </div>
                </div>
              </div> */}
              {errorMsg ? (
                <div className="form-div error-msg">
                  <PiWarningCircleLight /> {errorMsg}
                </div>
              ) : null}
              <br />
              <div className="form-div-row-error">
                <button className="sign-button" onClick={handleSubmit} disabled={loading}>
                  {loading ? (
                    <AiOutlineLoading3Quarters size={20} className="loading-spinner" />
                  ) : (
                    data.signup
                  )}
                </button>
              </div>
              <br />
              <div className="form-div-row error">
                <button
                  type="button"
                  className="sign-button3"
                  onClick={previousStep}
                >
                  {data.back}
                </button>
              </div>
            </form>
          )}
          {step === 5 && (
            <div className="sign-container-card-content">
              <img
                className="sponsorsImg"
                src="/images/signup.png"
                style={{ width: 276 }}
                alt=""
              />
              <p className="sign-info-text">{data.signed_info}</p>
              <div className="form-div signup-link">
                <Link to="/school/client" className="sign-text">
                  <button className="sign-button" type="button">
                    {data.continue}
                  </button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default SignUp;

